import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, XCircle, Clock, Home, Gift, Mail, Copy } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../config';
import { trackPixel } from '../lib/pixel';

const trackedVouchers = new Set();

const VoucherConfirmed = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [voucher, setVoucher] = useState(null);
  const [copied, setCopied] = useState(false);

  const voucherId = searchParams.get('voucher_id');

  useEffect(() => {
    if (voucherId) {
      const checkVoucherStatus = async () => {
        try {
          const response = await axios.get(`${API}/vouchers/${voucherId}/status`);
          setVoucher(response.data);

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            if (!trackedVouchers.has(voucherId)) {
              trackedVouchers.add(voucherId);
              trackPixel('Purchase', {
                value: response.data.amount,
                currency: 'EUR',
                content_name: 'Gift Voucher',
                content_category: 'gift_voucher',
              });
            }
          } else if (response.data.payment_status === 'failed') {
            setStatus('failed');
          } else {
            setStatus('pending');
            setTimeout(checkVoucherStatus, 3000);
          }
        } catch (error) {
          setStatus('success');
        }
      };
      checkVoucherStatus();
    } else {
      setStatus('success');
    }
  }, [voucherId]);

  const copyCode = () => {
    if (voucher?.code) {
      navigator.clipboard.writeText(voucher.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Clock size={40} className="text-amber-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Processing Payment...
            </h1>
            <p className="text-stone-600">Please wait while we confirm your purchase.</p>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-amber-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Almost There!
            </h1>
            <p className="text-stone-600 mb-4">
              Your payment is being processed. This page will update automatically.
            </p>
            <div className="animate-spin w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Payment Failed
            </h1>
            <p className="text-stone-600 mb-6">
              Unfortunately, your payment could not be processed. Please try again.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6"
            >
              Try Again
            </Button>
          </div>
        );

      case 'success':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-stone-800 mb-2">
              Gift Voucher Sent! 🎉
            </h1>
            <p className="text-stone-600 mb-6">
              The voucher has been emailed to the recipient. A copy has also been sent to you.
            </p>

            {voucher?.code && (
              <div className="bg-gradient-to-br from-sage-600 to-sage-700 rounded-2xl p-6 text-white mb-6">
                <p className="text-sage-200 text-sm mb-2">Voucher Code</p>
                <div className="flex items-center justify-center gap-3">
                  <p className="font-mono text-2xl sm:text-3xl tracking-wider">{voucher.code}</p>
                  <button
                    onClick={copyCode}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  >
                    {copied ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
                <p className="text-sage-200 text-sm mt-4">
                  Value: €{voucher.amount}
                </p>
              </div>
            )}

            <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Mail size={20} className="text-sage-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-stone-800 mb-1">Emails Sent</p>
                  <p className="text-stone-600">
                    The recipient will receive a beautifully designed voucher email with instructions on how to redeem it.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-full px-6"
              >
                <Home size={18} className="mr-2" />
                Back to Home
              </Button>
              <Button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('gift-vouchers')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                variant="outline"
                className="border-amber-500 text-amber-600 rounded-full px-6"
              >
                <Gift size={18} className="mr-2" />
                Buy Another
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sage-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-sage-700">Dublin Holistic Massage</h2>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-stone-500">
          <p>📞 +353 85 774 7474</p>
          <p>✉️ dublinholisticmassage@gmail.com</p>
        </div>
      </div>
    </div>
  );
};

export default VoucherConfirmed;
